// Items tab: positions of the cargo. Read-only card list here; the editable
// counterpart lives in DraftEditor (used while status === DRAFT).
// WHY (#1390): cards render on all viewports (desktop + mobile).
//   The legacy desktop table is preserved in ItemsDesktopTableLegacy.tsx
//   for quick rollback — it is not rendered here.

import { useState } from 'react';
import { Icon } from '../../../../ui/index.js';
import { useTranslation } from 'react-i18next';
import { CONTAINER_TOTALS_BACKFILL_STATUSES } from '@magimp/shared';
import type { RequestView, UserRef } from '../../types.js';
import { ContainerTotalsEditorCard } from '../ContainerTotalsEditorCard.js';
import { ItemMobileCard } from './ItemMobileCard.js';
import { RequestBoxesView } from './RequestBoxesView.js';
import { RequestDictionarySelectionsView } from '../RequestDictionarySelectionsView.js';
import { PhotoGallery } from '../PhotoGallery.js';
import { usePhotoDownloadResolver } from '../../photoDownloadResolver.js';

export interface ItemsTabProps {
  request: RequestView;
  me: UserRef;
}

export function ItemsTab({ request: r }: ItemsTabProps) {
  const { t } = useTranslation(['requests', 'common']);
  // WHY (#1451): the read-only photo viewer is the shared PhotoGallery,
  //   paging over ALL of the clicked item's photos. Track which item is
  //   open and the start index; `null` means the gallery is closed.
  const [gallery, setGallery] = useState<{
    itemId: string;
    keys: string[];
    startIndex: number;
  } | null>(null);
  // WHY (#2140): forward the snapshot photo resolver (null on the live view)
  //   to PhotoGallery, which cannot read the feature context itself (it is
  //   the narrow draft-media-capability).
  const photoResolver = usePhotoDownloadResolver();
  const boxes = r.boxes ?? [];
  // WHY (#996): the Roads/Packaging chips render in the cargo metadata
  //   section. Always visible (even on empty rows) so a manager can see
  //   the configured selection without opening the editor.
  const dictionarySelections = (
    <div style={{ padding: '12px 24px 0' }}>
      <RequestDictionarySelectionsView roads={r.roads ?? []} packaging={r.packaging ?? []} />
    </div>
  );
  // WHY (#2175): CONTAINER request-level totals can be backfilled post-send
  //   regardless of any preserved legacy item/box rows — render the editor
  //   in every branch, not only when cargo is empty.
  const showTotalsEditor =
    r.kind === 'CONTAINER' &&
    (CONTAINER_TOTALS_BACKFILL_STATUSES as readonly string[]).includes(r.status);
  const totalsEditor = showTotalsEditor ? (
    <div style={{ padding: '12px 24px 0' }}>
      <ContainerTotalsEditorCard request={r} />
    </div>
  ) : null;
  if (!r.items.length && boxes.length === 0) {
    return (
      <>
        {dictionarySelections}
        {totalsEditor}
        <Empty icon="package-open" text={t('requests:items.empty')} />
      </>
    );
  }
  // WHY (#599): boxes-only requests still render — items grid hides
  //   when empty, boxes view renders below. Drawer fetch always
  //   passes `?includeBoxes=true`; legacy callers leave `r.boxes`
  //   undefined so the slot renders nothing.
  if (!r.items.length) {
    return (
      <>
        {dictionarySelections}
        {totalsEditor}
        <div style={{ padding: '12px 24px 24px' }}>
          <RequestBoxesView
            requestId={r.id}
            boxes={boxes}
            regroup={{
              status: r.status,
              kind: r.kind,
              managerId: r.managerId,
              logistId: r.logistId,
            }}
          />
        </div>
      </>
    );
  }
  return (
    <>
      {dictionarySelections}
      {totalsEditor}
      <div style={{ padding: '12px 24px 24px' }}>
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {r.items.map((it) => (
            <li key={it.id} style={{ listStyle: 'none' }}>
              <ItemMobileCard
                requestId={r.id}
                itemId={it.id}
                name={it.name}
                description={it.description}
                brand={it.brand}
                material={it.material ?? ''}
                weight={it.weight}
                volume={it.volume}
                quantity={it.quantity}
                productQuantity={it.productQuantity}
                kind={r.kind}
                photoKeys={it.photoKeys}
                packages={it.packages ?? []}
                onOpenGallery={(startIndex) =>
                  setGallery({ itemId: it.id, keys: it.photoKeys, startIndex })
                }
              />
            </li>
          ))}
        </ul>
        <RequestBoxesView
          requestId={r.id}
          boxes={boxes}
          regroup={{
            status: r.status,
            kind: r.kind,
            managerId: r.managerId,
            logistId: r.logistId,
          }}
        />
        <PhotoGallery
          open={gallery !== null}
          onClose={() => setGallery(null)}
          keys={gallery?.keys ?? []}
          startIndex={gallery?.startIndex ?? 0}
          requestId={r.id}
          itemId={gallery?.itemId ?? ''}
          resolver={photoResolver}
        />
      </div>
    </>
  );
}

function Empty({
  icon,
  text,
}: {
  icon: 'package-open' | 'ship' | 'message-square' | 'scroll-text';
  text: string;
}) {
  return (
    <div
      style={{
        padding: '48px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        color: 'var(--fg-3)',
      }}
    >
      <Icon name={icon} size={24} style={{ color: 'var(--fg-4)' }} />
      <div style={{ fontSize: 13 }}>{text}</div>
    </div>
  );
}
